/**
 * useLiveUploadMonitor Hook - Sprint 10
 * 
 * Manages live upload progress state by listening to worker messages
 * Provides real-time upload status for LiveUploadMonitor component
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { LiveUploadProgress } from '../components/LiveUploadMonitor/LiveUploadMonitor';
import logger from '../utils/logger';

interface UploadWorkerMessage {
  type: string;
  videoGuid: string;
  progress?: number;
  speed?: number;
  eta?: number;
  uploadedChunks?: number;
  totalChunks?: number;
  chunkNo?: number;
  error?: string;
}

interface UseLiveUploadMonitorReturn {
  uploads: LiveUploadProgress[];
  updateProgress: (message: UploadWorkerMessage) => void;
  pauseUpload: (videoGuid: string) => void;
  resumeUpload: (videoGuid: string) => void;
  cancelUpload: (videoGuid: string) => void;
  clearCompletedUploads: () => void;
}

export const useLiveUploadMonitor = (
  onPauseUpload?: (videoGuid: string) => void,
  onResumeUpload?: (videoGuid: string) => void,
  onCancelUpload?: (videoGuid: string) => void
): UseLiveUploadMonitorReturn => {
  const [uploads, setUploads] = useState<Map<string, LiveUploadProgress>>(new Map());
  const uploadsRef = useRef<Map<string, LiveUploadProgress>>(new Map());

  // Sync ref with state
  useEffect(() => {
    uploadsRef.current = uploads;
  }, [uploads]);

  const updateProgress = useCallback((message: UploadWorkerMessage) => {
    const { type, videoGuid } = message;

    setUploads((prev) => {
      const updated = new Map(prev);
      const existing = updated.get(videoGuid);

      switch (type) {
        case 'upload-init-complete':
          // Initialize new upload
          updated.set(videoGuid, {
            videoGuid,
            progress: 0,
            speed: 0,
            eta: 0,
            uploadedChunks: 0,
            totalChunks: message.totalChunks || 0,
            status: 'uploading',
          });
          logger.info(`[LiveMonitor] Upload initialized: ${videoGuid}`);
          break;

        case 'upload-progress':
          // Update real-time progress
          if (existing) {
            updated.set(videoGuid, {
              ...existing,
              progress: message.progress || existing.progress,
              speed: message.speed || existing.speed,
              eta: message.eta || existing.eta,
              uploadedChunks: message.uploadedChunks || existing.uploadedChunks,
              totalChunks: message.totalChunks || existing.totalChunks,
              status: existing.status === 'paused' ? 'paused' : 'uploading',
            });
          }
          break;

        case 'chunk-uploaded':
          // Increment uploaded chunks count
          if (existing) {
            const uploadedChunks = (existing.uploadedChunks || 0) + 1;
            const progress = existing.totalChunks > 0 
              ? (uploadedChunks / existing.totalChunks) * 100 
              : 0;
            
            updated.set(videoGuid, {
              ...existing,
              uploadedChunks,
              progress,
            });
            logger.info(`[LiveMonitor] Chunk uploaded: ${videoGuid} (${uploadedChunks}/${existing.totalChunks})`);
          }
          break;

        case 'chunk-progress':
          // Update individual chunk progress (for large chunks)
          if (existing && message.chunkNo !== undefined) {
            const chunkProgress = message.progress || 0;
            logger.debug(`[LiveMonitor] Chunk ${message.chunkNo} progress: ${chunkProgress}%`);
          }
          break;

        case 'upload-paused':
          if (existing) {
            updated.set(videoGuid, {
              ...existing,
              status: 'paused',
            });
            logger.info(`[LiveMonitor] Upload paused: ${videoGuid}`);
          }
          break;

        case 'upload-resumed':
          if (existing) {
            updated.set(videoGuid, {
              ...existing,
              status: 'uploading',
            });
            logger.info(`[LiveMonitor] Upload resumed: ${videoGuid}`);
          }
          break;

        case 'upload-complete':
          if (existing) {
            updated.set(videoGuid, {
              ...existing,
              progress: 100,
              status: 'completed',
              eta: 0,
            });
            logger.info(`[LiveMonitor] Upload completed: ${videoGuid}`);
            
            // Auto-remove completed uploads after 5 seconds
            setTimeout(() => {
              setUploads((current) => {
                const newMap = new Map(current);
                newMap.delete(videoGuid);
                return newMap;
              });
            }, 5000);
          }
          break;

        case 'upload-error':
        case 'chunk-max-retries-exceeded':
          if (existing) {
            updated.set(videoGuid, {
              ...existing,
              status: 'error',
            });
            logger.error(`[LiveMonitor] Upload error: ${videoGuid}`, message.error);
          }
          break;

        case 'upload-cancelled':
        case 'abort-complete':
          // Remove cancelled uploads
          updated.delete(videoGuid);
          logger.info(`[LiveMonitor] Upload cancelled: ${videoGuid}`);
          break;

        default:
          // Ignore other message types
          break;
      }

      return updated;
    });
  }, []);

  const pauseUpload = useCallback((videoGuid: string) => {
    setUploads((prev) => {
      const updated = new Map(prev);
      const upload = updated.get(videoGuid);
      if (upload && upload.status === 'uploading') {
        updated.set(videoGuid, { ...upload, status: 'paused' });
        if (onPauseUpload) {
          onPauseUpload(videoGuid);
        }
        logger.info(`[LiveMonitor] Pausing upload: ${videoGuid}`);
      }
      return updated;
    });
  }, [onPauseUpload]);

  const resumeUpload = useCallback((videoGuid: string) => {
    setUploads((prev) => {
      const updated = new Map(prev);
      const upload = updated.get(videoGuid);
      if (upload && upload.status === 'paused') {
        updated.set(videoGuid, { ...upload, status: 'uploading' });
        if (onResumeUpload) {
          onResumeUpload(videoGuid);
        }
        logger.info(`[LiveMonitor] Resuming upload: ${videoGuid}`);
      }
      return updated;
    });
  }, [onResumeUpload]);

  const cancelUpload = useCallback((videoGuid: string) => {
    setUploads((prev) => {
      const updated = new Map(prev);
      updated.delete(videoGuid);
      if (onCancelUpload) {
        onCancelUpload(videoGuid);
      }
      logger.info(`[LiveMonitor] Cancelling upload: ${videoGuid}`);
      return updated;
    });
  }, [onCancelUpload]);

  const clearCompletedUploads = useCallback(() => {
    setUploads((prev) => {
      const updated = new Map(prev);
      Array.from(updated.entries()).forEach(([videoGuid, upload]) => {
        if (upload.status === 'completed') {
          updated.delete(videoGuid);
        }
      });
      logger.info(`[LiveMonitor] Cleared completed uploads`);
      return updated;
    });
  }, []);

  return {
    uploads: Array.from(uploads.values()),
    updateProgress,
    pauseUpload,
    resumeUpload,
    cancelUpload,
    clearCompletedUploads,
  };
};

export default useLiveUploadMonitor;
